import React from 'react';
import { usePermissions } from '../Context/PermissionContext';
import AppCache from '../Helper/Common/AppCache';

/**
 * Generic Permission Guard Component
 * Shows/hides content based on RBAC permissions with v1/v2 support
 */
export const Can = ({
  feature,
  operation = 'view',
  children,
  fallback = null,
  oldPermission = null
}) => {
  const { hasPermission, isRbacV2Enabled } = usePermissions();

  // V2: Use new RBAC system
  if (isRbacV2Enabled()) {
    if (!hasPermission(feature, operation)) {
      return fallback;
    }
    return <>{children}</>;
  }

  // V1: Use old permission system (if oldPermission provided)
  if (oldPermission) {
    const oldPermissions = AppCache.getItem('permissions') || [];
    if (!oldPermissions.includes(oldPermission)) {
      return fallback;
    }
  }

  return <>{children}</>;
};

/**
 * View Permission Guard
 * Shows content only if user has 'view' permission
 */
export const CanView = ({
  feature,
  children,
  fallback = null,
  oldPermission = null
}) => (
  <Can
    feature={feature}
    operation="view"
    oldPermission={oldPermission}
    fallback={fallback}
  >
    {children}
  </Can>
);

/**
 * Create Permission Guard
 * Shows content only if user has 'create' permission
 */
export const CanCreate = ({
  feature,
  children,
  fallback = null,
  oldPermission = null
}) => (
  <Can
    feature={feature}
    operation="create"
    oldPermission={oldPermission}
    fallback={fallback}
  >
    {children}
  </Can>
);

/**
 * Update Permission Guard
 * Shows content only if user has 'update' permission
 */
export const CanUpdate = ({
  feature,
  children,
  fallback = null,
  oldPermission = null
}) => (
  <Can
    feature={feature}
    operation="update"
    oldPermission={oldPermission}
    fallback={fallback}
  >
    {children}
  </Can>
);

/**
 * Delete Permission Guard
 * Shows content only if user has 'delete' permission
 */
export const CanDelete = ({
  feature,
  children,
  fallback = null,
  oldPermission = null
}) => (
  <Can
    feature={feature}
    operation="delete"
    oldPermission={oldPermission}
    fallback={fallback}
  >
    {children}
  </Can>
);

/**
 * Force Resolve Permission Guard
 * Shows content only if user has 'force_resolve' permission
 */
export const CanForceResolve = ({
  feature,
  children,
  fallback = null,
}) => (
  <Can
    feature={feature}
    operation="force_resolve"
    fallback={fallback}
  >
    {children}
  </Can>
);

/**
 * Multiple Permission Guard
 * Shows content if user has any of the specified permissions
 */
export const CanAny = ({
  feature,
  operations = ['view'],
  children,
  fallback = null
}) => {
  const { hasAnyPermission } = usePermissions();

  if (!hasAnyPermission(feature, operations)) {
    return fallback;
  }

  return <>{children}</>;
};

/**
 * Wrapper component that passes permission check result as render prop
 * Useful for complex conditional rendering
 */
export const WithPermission = ({
  feature,
  operation = 'view',
  children,
  oldPermission = null
}) => {
  const { hasPermission, isRbacV2Enabled } = usePermissions();

  let hasAccess = false;

  // V2: Check RBAC permission
  if (isRbacV2Enabled()) {
    hasAccess = hasPermission(feature, operation);
  }
  // V1: Check old permission
  else if (oldPermission) {
    const oldPermissions = AppCache.getItem('permissions') || [];
    hasAccess = oldPermissions.includes(oldPermission);
  }
  // V1: Default to true if no old permission specified
  else {
    hasAccess = true;
  }

  return <>{typeof children === 'function' ? children(hasAccess) : children}</>;
};
